import type {
    MarketingAdCampaignRow,
    MarketingAdStats,
    MarketingCampaignRow,
} from '@/lib/api/marketing/marketing-dashboard-api'
import { Fragment, useState } from 'react'
import { MdChevronRight, MdHelpOutline } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import DeltaBadge from './delta-badge'
import { deltaOf, formatCurrency, formatNumber, formatPercent } from './marketing-format'

const STATUS_LABELS: Record<string, string> = {
    DRAFT: 'Rascunho',
    ACTIVE: 'Ativa',
    PAUSED: 'Pausada',
    FINISHED: 'Encerrada',
}

/** Campanhas próprias: links rastreados do sistema (dexlnk). */
export function OwnCampaignsTable({
    current,
    previous,
}: {
    current: MarketingCampaignRow[]
    previous: MarketingCampaignRow[] | null
}) {
    const navigate = useNavigate()

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
            <h3 className="text-lg font-semibold text-base-content">Campanhas próprias</h3>
            <p className="text-sm text-base-content/60 mb-4">Links rastreados criados em Marketing &gt; Campanhas</p>
            {current.length === 0 ? (
                <p className="text-center text-base-content/50 py-8">Nenhuma campanha com movimento no período</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Campanha</th>
                                <th className="text-right">Cliques</th>
                                <th className="text-right">Leads</th>
                                <th className="text-right">Clique → lead</th>
                                <th className="text-right">Vendas</th>
                                <th className="text-right">Receita</th>
                            </tr>
                        </thead>
                        <tbody>
                            {current.map(c => {
                                const prev = previous?.find(p => p.id === c.id)
                                return (
                                    <tr
                                        key={c.id}
                                        className="hover cursor-pointer"
                                        onClick={() => navigate(`/marketing/campaigns/${c.id}`)}
                                    >
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">{c.name}</span>
                                                <span className="badge badge-xs badge-ghost">
                                                    {STATUS_LABELS[c.status] ?? c.status}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {formatNumber(c.clicks)}
                                                {previous && <DeltaBadge delta={deltaOf(c.clicks, prev?.clicks ?? 0)} />}
                                            </div>
                                        </td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {formatNumber(c.leads)}
                                                {previous && <DeltaBadge delta={deltaOf(c.leads, prev?.leads ?? 0)} />}
                                            </div>
                                        </td>
                                        <td className="text-right">
                                            {formatPercent(c.clicks ? (c.leads / c.clicks) * 100 : null)}
                                        </td>
                                        <td className="text-right">{formatNumber(c.salesCount)}</td>
                                        <td className="text-right whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {formatCurrency(c.salesValue)}
                                                {previous && <DeltaBadge delta={deltaOf(c.salesValue, prev?.salesValue ?? 0)} />}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

/** O que a plataforma conta como resultado: leads de formulário/site + conversas iniciadas. */
const platformResultsOf = (r: MarketingAdStats) => r.platformLeads + r.platformConversations

/** Quanto do que a plataforma contou chegou ao sistema. Acima de 100% é normal em
 * conversa: o mesmo contato que volta dias depois não é contado de novo pela Meta. */
const arrivalRateOf = (r: MarketingAdStats) => {
    const results = platformResultsOf(r)
    return results ? (r.leads / results) * 100 : null
}

function AdStatsCells({ r, prev, showPlatform }: { r: MarketingAdStats; prev?: MarketingAdStats | null; showPlatform: boolean }) {
    const arrival = arrivalRateOf(r)
    return (
        <>
            {showPlatform && (
                <>
                    <td className="text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                            {r.spend ? formatCurrency(r.spend) : '—'}
                            {prev !== undefined && r.spend > 0 && (
                                <DeltaBadge delta={deltaOf(r.spend, prev?.spend ?? 0, 'neutral')} />
                            )}
                        </div>
                    </td>
                    <td className="text-right">{r.impressions ? formatNumber(r.impressions) : '—'}</td>
                    <td className="text-right">{r.clicks ? formatNumber(r.clicks) : '—'}</td>
                    <td className="text-right font-medium">{platformResultsOf(r) ? formatNumber(platformResultsOf(r)) : '—'}</td>
                    <td className="text-right">
                        {r.clicks ? formatPercent((platformResultsOf(r) / r.clicks) * 100) : '—'}
                    </td>
                </>
            )}
            <td className="text-right font-medium">
                <div className="flex items-center justify-end gap-1.5">
                    {formatNumber(r.leads)}
                    {prev !== undefined && <DeltaBadge delta={deltaOf(r.leads, prev?.leads ?? 0)} />}
                </div>
            </td>
            {showPlatform && (
                <td className="text-right">
                    {arrival == null ? (
                        '—'
                    ) : (
                        <span className={arrival < 50 ? 'text-error font-semibold' : arrival < 80 ? 'text-warning font-semibold' : ''}>
                            {formatPercent(arrival)}
                        </span>
                    )}
                </td>
            )}
            {showPlatform && (
                <td className="text-right whitespace-nowrap">
                    {r.spend && r.leads ? formatCurrency(Math.round(r.spend / r.leads)) : '—'}
                </td>
            )}
            <td className="text-right">{formatNumber(r.salesCount)}</td>
            <td className="text-right whitespace-nowrap">
                <div className="flex items-center justify-end gap-1.5">
                    {formatCurrency(r.salesValue)}
                    {prev !== undefined && <DeltaBadge delta={deltaOf(r.salesValue, prev?.salesValue ?? 0)} />}
                </div>
            </td>
            {showPlatform && (
                <td className="text-right whitespace-nowrap">
                    {r.spend ? `${(r.salesValue / r.spend).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}x` : '—'}
                </td>
            )}
        </>
    )
}

/**
 * Campanhas de anúncio: o que a PLATAFORMA conta (gasto, impressões, cliques,
 * resultados) ao lado do que CHEGOU ao sistema (leads com o rastro do anúncio) e do que
 * virou venda. A "chegada" é a pergunta do marketing: a Meta diz 50 conversas, quantas
 * viraram lead aqui? Cada campanha abre nos anúncios.
 */
export function AdCampaignsTable({
    title,
    subtitle,
    platform,
    current,
    previous,
    synced,
    onHelp,
}: {
    title: string
    subtitle: string
    platform: 'META' | 'GOOGLE'
    current: MarketingAdCampaignRow[]
    previous: MarketingAdCampaignRow[] | null
    /** Há métricas sincronizadas da plataforma (sem isso, só o que chegou ao sistema). */
    synced: boolean
    onHelp?: () => void
}) {
    const [open, setOpen] = useState<Set<string>>(new Set())
    const toggle = (key: string) =>
        setOpen(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })

    const sum = (rows: MarketingAdCampaignRow[] | null, pick: (r: MarketingAdCampaignRow) => number) =>
        rows ? rows.reduce((acc, r) => acc + pick(r), 0) : null
    const totalSpend = sum(current, r => r.spend) ?? 0
    const totalResults = sum(current, platformResultsOf) ?? 0
    const totalLeads = sum(current, r => r.leads) ?? 0
    const totalSales = sum(current, r => r.salesValue) ?? 0

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-2">
                    <div>
                        <h3 className="text-lg font-semibold text-base-content">{title}</h3>
                        <p className="text-sm text-base-content/60">{subtitle}</p>
                    </div>
                    {onHelp && (
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs btn-circle text-base-content/40 hover:text-base-content"
                            onClick={onHelp}
                            aria-label="Como ler esta tabela?"
                            title="Como ler esta tabela?"
                        >
                            <MdHelpOutline className="text-base" />
                        </button>
                    )}
                </div>
                <div className="flex flex-wrap gap-5 text-right">
                    {synced && (
                        <>
                            <div>
                                <p className="text-xs text-base-content/60">Investimento</p>
                                <p className="flex items-center justify-end gap-1.5 font-semibold">
                                    {formatCurrency(totalSpend)}
                                    <DeltaBadge delta={deltaOf(totalSpend, sum(previous, r => r.spend), 'neutral')} />
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-base-content/60">Resultados na plataforma</p>
                                <p className="font-semibold">{formatNumber(totalResults)}</p>
                            </div>
                        </>
                    )}
                    <div>
                        <p className="text-xs text-base-content/60">Chegaram ao sistema</p>
                        <p className="flex items-center justify-end gap-1.5 font-semibold">
                            {formatNumber(totalLeads)}
                            {synced && totalResults > 0 && (
                                <span className="text-xs font-normal text-base-content/60">
                                    ({formatPercent((totalLeads / totalResults) * 100)})
                                </span>
                            )}
                            <DeltaBadge delta={deltaOf(totalLeads, sum(previous, r => r.leads))} />
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-base-content/60">Receita</p>
                        <p className="flex items-center justify-end gap-1.5 font-semibold">
                            {formatCurrency(totalSales)}
                            <DeltaBadge delta={deltaOf(totalSales, sum(previous, r => r.salesValue))} />
                        </p>
                    </div>
                </div>
            </div>
            {current.length === 0 ? (
                <p className="text-center text-base-content/50 py-8">
                    {synced ? 'Nenhum anúncio com movimento no período' : 'Nenhum lead de anúncio chegou no período'}
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Campanha</th>
                                {synced && <th className="text-right">Investimento</th>}
                                {synced && <th className="text-right">Impressões</th>}
                                {synced && <th className="text-right">Cliques</th>}
                                {synced && <th className="text-right" title="Leads e conversas que a plataforma conta">Na plataforma</th>}
                                {synced && (
                                    <th className="text-right" title="Resultados / cliques: quem tocou no anúncio e chegou a escrever">
                                        Clique → conversa
                                    </th>
                                )}
                                <th className="text-right" title="Leads com o rastro do anúncio no sistema">No sistema</th>
                                {synced && <th className="text-right" title="No sistema / na plataforma">Chegada</th>}
                                {synced && <th className="text-right" title="Investimento / leads no sistema">CPL</th>}
                                <th className="text-right">Vendas</th>
                                <th className="text-right">Receita</th>
                                {synced && <th className="text-right" title="Receita / investimento">ROAS</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {current.map(r => {
                                const key = r.campaignId ?? 'none'
                                const prev = previous ? previous.find(p => p.campaignId === r.campaignId) ?? null : undefined
                                const expandable = r.ads.length > 0 && !(r.ads.length === 1 && r.ads[0].adId == null)
                                const isOpen = open.has(key)
                                return (
                                    <Fragment key={key}>
                                        <tr
                                            className={expandable ? 'hover cursor-pointer' : ''}
                                            onClick={expandable ? () => toggle(key) : undefined}
                                        >
                                            <td>
                                                <div className="flex items-start gap-1">
                                                    {expandable ? (
                                                        <MdChevronRight
                                                            className={`mt-0.5 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                                                        />
                                                    ) : (
                                                        <span className="w-4 shrink-0" />
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="font-medium truncate max-w-[18rem]" title={r.campaignName ?? undefined}>
                                                            {r.campaignName ??
                                                                (r.campaignId ? (
                                                                    <span className="font-mono text-xs">{r.campaignId}</span>
                                                                ) : (
                                                                    <span className="text-base-content/60">
                                                                        {platform === 'META'
                                                                            ? 'Anúncio sem campanha identificada'
                                                                            : 'Sem campanha (só o clique)'}
                                                                    </span>
                                                                ))}
                                                        </p>
                                                        {platform === 'META' && (r.clickToWhatsappLeads > 0 || r.formLeads > 0) && (
                                                            <p className="text-[11px] text-base-content/50">
                                                                {r.clickToWhatsappLeads > 0 && `${formatNumber(r.clickToWhatsappLeads)} pelo WhatsApp`}
                                                                {r.clickToWhatsappLeads > 0 && r.formLeads > 0 && ' · '}
                                                                {r.formLeads > 0 && `${formatNumber(r.formLeads)} por formulário`}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <AdStatsCells r={r} prev={prev} showPlatform={synced} />
                                        </tr>
                                        {isOpen &&
                                            r.ads.map(ad => (
                                                <tr key={`${key}:${ad.adId ?? 'none'}`} className="bg-base-200/40 text-xs">
                                                    <td className="pl-9">
                                                        <span className="truncate block max-w-[16rem]" title={ad.adName ?? ad.adId ?? undefined}>
                                                            {ad.adName ?? ad.adId ?? (
                                                                <span className="text-base-content/60">Lead sem anúncio identificado</span>
                                                            )}
                                                        </span>
                                                        {ad.adsetName && (
                                                            <span
                                                                className="truncate block max-w-[16rem] text-[11px] text-base-content/50"
                                                                title={ad.adsetName}
                                                            >
                                                                {ad.adsetName}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <AdStatsCells r={ad} showPlatform={synced} />
                                                </tr>
                                            ))}
                                    </Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                    {synced && (
                        <p className="mt-2 text-[11px] text-base-content/50">
                            Clique → conversa: quem tocou no anúncio e chegou a escrever. O resto abriu o WhatsApp e não
                            mandou mensagem; nenhum sistema recebe esse contato. Chegada: das conversas que a Meta conta, quantas
                            viraram lead aqui (abaixo de 50% em vermelho: número sem a API oficial ou formulário sem integração).
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}
